import React from 'react';
import { Link } from 'react-router-dom';
import { AccessibilityIcon, ArrowRightIcon, BadgeCheckIcon, MailIcon, MapPinIcon, PhoneIcon } from 'lucide-react';
import { site, publics, raisons, methodes, parcours } from '../data/site';
import { formations } from '../data/formations';
import { FormationCard } from '../components/FormationCard';
import { SectionHeading } from '../components/Section';
import { QualiopiBadge } from '../components/QualiopiBadge';
import { useSeo } from '../hooks/useSeo';
export function Home() {
  useSeo({
    title: 'Zillenium Academy — Formation professionnelle à Joigny (Yonne) | Organisme certifié Qualiopi',
    description: 'Organisme de formation professionnelle certifié Qualiopi à Joigny (89). Formations digitale, réseaux sociaux, commerce, point de vente, management et entrepreneuriat pour entreprises, indépendants, salariés et associations de l’Yonne.'
  });
  return <>
      {/* Hero */}
      <section className="bg-brand-900" data-id="element-355" data-mp="3oyq9zm84gi9t">
        <div className="mx-auto grid max-w-content items-center gap-12 px-5 py-16 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16 lg:px-8 lg:py-24" data-id="element-356" data-mp="30x1j0f4e8oqi">
          <div data-id="element-357" data-mp="2vyhrlz716kqr">
            <QualiopiBadge tone="dark" data-id="element-358" data-mp="2vvvoqcv34tev" />
            <h1 className="mt-6 text-4xl font-extrabold leading-[1.08] text-white md:text-[54px]" data-id="element-359" data-mp="2qxu43ni0gm1a">
              Développez les compétences
              <span className="block text-accent-400" data-id="element-360" data-mp="2mwqmavgekfxf">qui font avancer votre activité.</span>
            </h1>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-brand-200" data-id="element-361" data-mp="2i007ebgc3qdl">
              Zillenium Academy est un organisme de formation professionnelle certifié Qualiopi,
              basé à {site.ville} dans l’{site.departement}. Digital, commerce, management,
              entrepreneuriat : des parcours concrets, construits autour de vos situations de
              travail réelles.
            </p>

            <div className="mt-9 flex flex-col gap-3 sm:flex-row" data-id="element-362" data-mp="1u8cqmmcsdvp1">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent-500 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-accent-600" data-id="element-363" data-mp="1u94mzze7jxgd">
                Demander un devis
                <ArrowRightIcon size={18} aria-hidden data-id="element-364" data-mp="1aodxsts0ympj" />
              </Link>
              <Link to="/formations" className="inline-flex items-center justify-center rounded-lg border border-white/25 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-white/10" data-id="element-365" data-mp="1fmfex95w4byd">
                Voir les 4 formations
              </Link>
            </div>

            <p className="mt-8 inline-flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-brand-200" data-id="element-366" data-mp="1yki4pq69icx8">
              <span className="inline-flex items-center gap-2" data-id="element-367" data-mp="2macgs5irbxgf">
                <PhoneIcon size={15} className="text-accent-400" aria-hidden data-id="element-368" data-mp="2m7ztrdy56wwi" />
                <a href={`tel:${site.telephoneLien}`} className="hover:text-white" data-id="element-369" data-mp="2r2dqbjojja2r">
                  {site.telephone}
                </a>
              </span>
              <span className="inline-flex items-center gap-2" data-id="element-370" data-mp="an92t718pquy">
                <MailIcon size={15} className="text-accent-400" aria-hidden data-id="element-371" data-mp="akwm0x77xmjh" />
                <a href={`mailto:${site.email}`} className="hover:text-white" data-id="element-372" data-mp="odm5atvd4dcw">
                  {site.email}
                </a>
              </span>
            </p>
          </div>

          <div data-id="element-373" data-mp="3st58tc3hhy5l">
            <img src={site.heroImage} alt="Formatrice Zillenium Academy animant une session auprès de professionnels" className="aspect-[4/3] w-full rounded-2xl object-cover" data-id="element-374" data-mp="3ssvpu8guwr9e" />
          </div>
        </div>
      </section>

      {/* Présentation */}
      <section className="border-b border-surface-line bg-white" data-id="element-375" data-mp="n8d93cs2eib">
        <div className="mx-auto grid max-w-content gap-10 px-5 py-16 lg:grid-cols-[1.1fr_0.9fr] lg:px-8" data-id="element-376" data-mp="1h1rubstg8oij">
          <div data-id="element-377" data-mp="1c2oquct4aqco">
            <h2 className="text-3xl font-bold leading-tight text-ink md:text-[38px]" data-id="element-378" data-mp="1c36qljeznmpc">
              Un organisme de formation à taille humaine, dans l’Yonne
            </h2>
            <div className="mt-5 space-y-4 text-[17px] leading-relaxed text-ink-muted" data-id="element-379" data-mp="2ce12ghwbmdpo">
              <p data-id="element-380" data-mp="2hbt5o82f1m96">
                Installés au {site.adresse}, nous accompagnons les entreprises, commerces,
                indépendants et associations du territoire sur les compétences qui comptent
                aujourd’hui : transformation digitale, communication, commerce, management et
                entrepreneuriat.
              </p>
              <p data-id="element-381" data-mp="2va0uttt7nj9s">
                Chaque demande commence par un échange : nous identifions vos objectifs, le profil
                des bénéficiaires et les éventuels besoins d’adaptation avant de proposer un
                programme et un devis.
              </p>
            </div>
          </div>
          <div className="rounded-xl border border-surface-line bg-surface-soft p-6" data-id="element-382" data-mp="3a2kv8moz7cnj">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-ink-faint" data-id="element-383" data-mp="3e2v0ty8uw11v">
              Nos formations s’adressent à
            </h3>
            <ul className="mt-4 flex flex-wrap gap-2" data-id="element-384" data-mp="ydms1xp5mrs6">
              {publics.map(p => <li key={p} className="rounded-full border border-surface-line bg-white px-3.5 py-1.5 text-sm font-medium text-brand-800" data-id="element-385" data-mp="te1t3n9zijfg">
                  {p}
                </li>)}
            </ul>
          </div>
        </div>
      </section>

      {/* Nos formations */}
      <section className="mx-auto max-w-content px-5 py-20 lg:px-8" data-id="element-386" data-mp="1h579c3ju7dqa">
        <SectionHeading title="Nos formations" intro="Quatre parcours professionnels, du digital au management, conçus pour être appliqués dès le retour au travail." action={<Link to="/formations" className="inline-flex items-center gap-2 rounded-lg border border-surface-line bg-white px-5 py-3 text-sm font-semibold text-brand-800 transition-colors duration-150 ease-out hover:bg-surface-soft" data-id="element-388" data-mp="24tqgugv2bmhb">
              Voir toutes les formations
              <ArrowRightIcon size={16} aria-hidden data-id="element-389" data-mp="25i5kkhds7ue" />
            </Link>} data-id="element-387" data-mp="1ccmgb48r94nt" />
        <div className="mt-10 grid gap-6 md:grid-cols-2" data-id="element-390" data-mp="3kjq1nb1gtysk">
          {formations.map(f => <FormationCard key={f.slug} formation={f} data-id="element-391" data-mp="3piu7pst3fgeo" />)}
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section className="bg-surface-soft" data-id="element-392" data-mp="3ala8z3msihgy">
        <div className="mx-auto max-w-content px-5 py-20 lg:px-8" data-id="element-393" data-mp="3fm7o17ci6ve8">
          <SectionHeading title="Pourquoi choisir Zillenium Academy ?" intro="Un cadre exigeant, une organisation souple et un vrai ancrage local." data-id="element-394" data-mp="3fjbq3j0no6ps" />
          <div className="mt-12 grid gap-x-12 gap-y-9 md:grid-cols-2 lg:grid-cols-3" data-id="element-395" data-mp="36l9tdfnyg08b">
            {raisons.map(r => <article key={r.titre} className="border-t-2 border-accent-500 pt-5" data-id="element-396" data-mp="zspfio83yn40">
                <h3 className="text-lg font-bold text-ink" data-id="element-397" data-mp="14rka0nffu03y">{r.titre}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-398" data-mp="1eyd35gnqd3eh">{r.texte}</p>
              </article>)}
          </div>
        </div>
      </section>

      {/* Méthode pédagogique */}
      <section className="mx-auto max-w-content px-5 py-20 lg:px-8" data-id="element-399" data-mp="1nrge9iawl45y">
        <div className="grid gap-12 lg:grid-cols-[0.85fr_1.15fr] lg:items-start" data-id="element-400" data-mp="1iyx5cyn5ux7h">
          <div data-id="element-401" data-mp="1xnjrpkb7gp87">
            <SectionHeading title="Notre méthode pédagogique" intro="Peu de théorie descendante, beaucoup de pratique sur vos propres dossiers." data-id="element-402" data-mp="1xn09e2hn7ytn" />
            <img src={site.imageApproche} alt="Atelier collectif animé par Zillenium Academy" className="mt-8 aspect-[4/3] w-full rounded-2xl object-cover" data-id="element-403" data-mp="26p9g0h9sj81r" />
          </div>
          <ul className="grid gap-4 sm:grid-cols-2" data-id="element-404" data-mp="cbi1b3cltxzg">
            {methodes.map(m => <li key={m.titre} className="rounded-xl border border-surface-line bg-white p-6" data-id="element-405" data-mp="8a6grc8l82y4">
                <h3 className="text-lg font-bold text-ink" data-id="element-406" data-mp="wt0fwpyiqxu2">{m.titre}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-ink-muted" data-id="element-407" data-mp="11smqssqvk1zp">{m.texte}</p>
              </li>)}
          </ul>
        </div>
      </section>

      {/* Accompagnement */}
      <section className="bg-brand-900" data-id="element-408" data-mp="1al8782719wld">
        <div className="mx-auto max-w-content px-5 py-20 lg:px-8" data-id="element-409" data-mp="1akyg2ba95je3">
          <SectionHeading tone="dark" title="Comment nous vous accompagnons" intro="De la première prise de contact aux documents remis en fin de parcours, chaque étape est cadrée." data-id="element-410" data-mp="dvqwh8t71kct" />
          <ol className="mt-12 grid gap-x-8 gap-y-10 md:grid-cols-2 lg:grid-cols-4" data-id="element-411" data-mp="muc7miwc5x3k">
            {parcours.map((e, i) => <li key={e.titre} data-id="element-412" data-mp="rsvviouhpiyk" data-mpi={i}>
                <div className="flex items-center gap-3" data-id="element-413" data-mp="1ybkb91z5r96o">
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-white" data-id="element-414" data-mp="1yd57qx519276">
                    {i + 1}
                  </span>
                  <span className="h-px flex-1 bg-white/15" aria-hidden data-id="element-415" data-mp="2dg6kph2gwmv7" />
                </div>
                <h3 className="mt-5 text-lg font-bold text-white" data-id="element-416" data-mp="28h2e8y6xh38k">{e.titre}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-brand-200" data-id="element-417" data-mp="23mps2ge2hdt4">{e.texte}</p>
              </li>)}
          </ol>
        </div>
      </section>

      {/* Qualiopi + accessibilité */}
      <section className="mx-auto max-w-content px-5 py-20 lg:px-8" data-id="element-418" data-mp="1uovyvebln0a6">
        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]" data-id="element-419" data-mp="1pngdfl5ierr9">
          <article className="rounded-2xl border border-surface-line bg-surface-soft p-8" data-id="element-420" data-mp="1kpewp6vk34iy">
            <BadgeCheckIcon size={30} className="text-accent-500" aria-hidden data-id="element-421" data-mp="1gnta2tt7zbvk" />
            <h2 className="mt-4 text-2xl font-bold text-ink" data-id="element-422" data-mp="1gpwam7s00nej">
              Un organisme de formation certifié Qualiopi
            </h2>
            <p className="mt-3 max-w-2xl text-[17px] leading-relaxed text-ink-muted" data-id="element-423" data-mp="3b2421b542m5k">
              La certification Qualiopi atteste de la qualité du processus mis en œuvre pour nos
              actions de formation. Concrètement : une information claire et vérifiable avant
              l’inscription, des objectifs pédagogiques définis, des évaluations formalisées et
              l’analyse des retours des bénéficiaires afin d’ajuster les prestations lorsque cela
              est nécessaire.
            </p>
            <Link to="/qualiopi" className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-accent-600 hover:text-accent-700" data-id="element-424" data-mp="3q2q1fj9wbfkd">
              Notre démarche qualité
              <ArrowRightIcon size={16} aria-hidden data-id="element-425" data-mp="3oon6uapeyr3" />
            </Link>
          </article>

          <article className="rounded-2xl border border-surface-line bg-white p-8" data-id="element-426" data-mp="3v1stm34s1rax">
            <AccessibilityIcon size={30} className="text-brand-700" aria-hidden data-id="element-427" data-mp="37c8btnkd1038" />
            <h2 className="mt-4 text-2xl font-bold text-ink" data-id="element-428" data-mp="32ef4gr7bm01d">Accessibilité & handicap</h2>
            <p className="mt-3 text-[17px] leading-relaxed text-ink-muted" data-id="element-429" data-mp="1hw4mh1xkag19">
              Zillenium Academy étudie les aménagements possibles en fonction des besoins du
              bénéficiaire. Signalez votre situation avant l’inscription.
            </p>
            <a href={`mailto:${site.refHandicap}`} className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand-800 hover:text-accent-600" data-id="element-430" data-mp="18x12lvqklwlp">
              <MailIcon size={16} aria-hidden data-id="element-431" data-mp="20n5grcnqlneh" />
              Référent handicap : {site.refHandicap}
            </a>
          </article>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-surface-line bg-surface-soft" data-id="element-432" data-mp="1rkw8yujus84s">
        <div className="mx-auto flex max-w-content flex-col items-start gap-8 px-5 py-16 lg:flex-row lg:items-center lg:justify-between lg:px-8" data-id="element-433" data-mp="1wjzfykje8ofe">
          <div className="max-w-2xl" data-id="element-434" data-mp="g8lorcj6dkby">
            <h2 className="text-3xl font-bold leading-tight text-ink md:text-4xl" data-id="element-435" data-mp="l651u944zr2b">
              Parlons de votre besoin de formation
            </h2>
            <p className="mt-3 text-lg text-ink-muted" data-id="element-436" data-mp="2mtzt8zt4blbg">
              Demandez un devis ou faites-vous recontacter : nous analysons votre besoin avant toute
              proposition.
            </p>
            <p className="mt-4 inline-flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-ink-muted" data-id="element-437" data-mp="2d3585yrlyrrl">
              <span className="inline-flex items-center gap-2" data-id="element-438" data-mp="291a80xleykfe">
                <MapPinIcon size={15} className="text-accent-600" aria-hidden data-id="element-439" data-mp="245bfqywysi9p" />
                {site.adresse}
              </span>
              <span className="inline-flex items-center gap-2" data-id="element-440" data-mp="1z79uc7c2g6v2">
                <PhoneIcon size={15} className="text-accent-600" aria-hidden data-id="element-441" data-mp="1u75b49jrwy15" />
                <a href={`tel:${site.telephoneLien}`} className="hover:text-brand-800" data-id="element-442" data-mp="1q50i9d15g5fk">
                  {site.telephone}
                </a>
              </span>
            </p>
          </div>
          <Link to="/contact" className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-brand-900 px-6 py-3.5 text-base font-semibold text-white transition-colors duration-150 ease-out hover:bg-brand-800" data-id="element-443" data-mp="3ki06fujli7yg">
            Demander un devis
            <ArrowRightIcon size={18} aria-hidden data-id="element-444" data-mp="1g0k5wtmvp6gi" />
          </Link>
        </div>
      </section>
    </>;
}